import { useEffect, useRef, useState } from 'react';
import { Button, Icon, IconButton, Modal, Popover, Toggle } from '../ui';
import { useIsPhone } from '../../app/useMediaQuery';
import type { BasemapId } from '../../lib/types';

const BASEMAPS: Array<{ id: BasemapId; label: string; swatch: string }> = [
  { id: 'dark', label: 'Dark', swatch: 'map-swatch--dark' },
  { id: 'daylight', label: 'Daylight', swatch: 'map-swatch--daylight' },
  { id: 'satellite', label: 'Imagery (US only)', swatch: 'map-swatch--satellite' },
];

export interface LayerSwitch {
  on: boolean;
  onChange(v: boolean): void;
  /** Name of the viewing mode that hides this layer right now (LIVE), e.g. "Track". */
  hiddenBy?: string;
}

function LayerToggle({ sw, label }: { sw: LayerSwitch; label: string }) {
  return (
    <div className="map-layers__row">
      <Toggle checked={sw.on && !sw.hiddenBy} onChange={sw.onChange} label={label} disabled={!!sw.hiddenBy} />
      {sw.hiddenBy ? <span className="map-layers__why">Hidden in {sw.hiddenBy} view</span> : null}
    </div>
  );
}

/**
 * The traffic provider's note, minus the operator diagnostics (request counters, env variable names,
 * polling internals) — those stay in the tooltip for whoever is debugging a feed.
 */
export function trafficNoteSummary(note: string | null | undefined): string | null {
  if (!note) return null;
  const sentences = note.split(/(?<=\.)\s+/).map((x) => x.trim()).filter(Boolean);
  const plain = sentences.filter((x) => !/APOS_|requests?\b|rate-limited|Polled|gives way|Not configured|yielded/i.test(x));
  return plain.join(' ') || null;
}

/** Map key: what the aircraft symbols mean (drawn like the map draws them). */
function Legend({ showTraffic }: { showTraffic: boolean }) {
  const rows: Array<[string, string, string]> = [
    ['subject', 'Subject', 'M12 2.5c.9 0 1.1 2.2 1.1 5.4l7.4 1v1.8l-7.5.3-.3 4.6 3.2 2.3v1.3L12 18.2l-3.9 1v-1.3l3.2-2.3-.3-4.6-7.5-.3V8.9l7.4-1C10.9 4.7 11.1 2.5 12 2.5z'],
    ['chase', 'Chase', 'M12 4.2a3 4.6 0 1 1 0 9.2 3 4.6 0 1 1 0-9.2zM11.2 13h1.6l.2 5.4 2.4.6v1.3H8.6V19l2.4-.6zM12 1.5a9 9 0 1 1 0 18 9 9 0 1 1 0-18z'],
    ['watch', 'Watch', 'M12 4l6 15.5-6-3.8-6 3.8z'],
  ];
  if (showTraffic) rows.push(['traffic', 'Other traffic', 'M12 7l4 10-4-2.5L8 17z']);
  return (
    <div className="map-legend" data-testid="map-legend">
      {rows.map(([k, label, d]) => (
        <div key={k} className={`map-legend__row map-legend__row--${k}`}>
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
            <path d={d} fill={k === 'chase' ? 'none' : 'currentColor'} stroke="currentColor" strokeWidth={k === 'chase' ? 1.4 : 0} fillRule="evenodd" />
            {k === 'chase' ? <ellipse cx="12" cy="8.8" rx="2.4" ry="3.8" fill="currentColor" /> : null}
          </svg>
          <span>{label}</span>
        </div>
      ))}
    </div>
  );
}

export function MapControls(props: {
  expanded?: boolean;
  onToggleExpanded?(): void;
  bearing: number;
  basemap: BasemapId;
  trafficNote?: string | null;
  following: boolean;
  trackUp: boolean;
  /** 2D/3D view (VLP-2300); omitted = no toggle. */
  viewMode?: '2d' | '3d';
  onViewMode?(mode: '2d' | '3d'): void;
  /** Optional layer switches shown under the basemaps (null = not offered on this map). */
  layers?: { traffic: LayerSwitch | null; flightPath: LayerSwitch | null };
  onZoom(d: number): void;
  onNorth(): void;
  onToggleTrackUp(): void;
  onFit(): void;
  onBasemap(b: BasemapId): void;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open || !props.expanded) return;
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); }
    };
    document.addEventListener('keydown', escape, true);
    return () => document.removeEventListener('keydown', escape, true);
  }, [open, props.expanded]);
  const [details, setDetails] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const rotated = Math.abs(props.bearing) > 0.5;
  const phone = useIsPhone();
  const note = trafficNoteSummary(props.trafficNote);
  const showLayers = !!(props.layers?.traffic || props.layers?.flightPath);
  const body = (
    <>
      <div className="menu-label eyebrow">Basemap</div>
      <div className="map-basemaps__grid" role="group" aria-label="Basemap">
        {BASEMAPS.map((b) => (
          <button
            key={b.id}
            type="button"
            className={`map-basemap${b.id === props.basemap ? ' is-on' : ''}`}
            aria-pressed={b.id === props.basemap}
            onClick={() => {
              props.onBasemap(b.id);
              setOpen(false);
            }}
          >
            <span className={`map-swatch ${b.swatch}`} />
            <span>{b.label}</span>
            {b.id === props.basemap ? <Icon name="check" size={16} /> : null}
          </button>
        ))}
      </div>
      {showLayers ? (
        <div className="map-layers" data-testid="map-layers">
          <div className="menu-label eyebrow">Show</div>
          {props.layers!.traffic ? <LayerToggle sw={props.layers!.traffic} label="Other traffic" /> : null}
          {props.layers!.flightPath ? <LayerToggle sw={props.layers!.flightPath} label="Flight paths" /> : null}
          {note && props.layers!.traffic ? <p className="map-layers__note">{note}</p> : null}
          {props.trafficNote && props.layers!.traffic && props.trafficNote !== note ? (
            <>
              <Button variant="link" size="sm" className="map-layers__more" aria-expanded={details} onClick={() => setDetails((v) => !v)}>
                {details ? 'Hide source details' : 'Source details'}
              </Button>
              {details ? <p className="map-layers__note map-layers__note--detail">{props.trafficNote}</p> : null}
            </>
          ) : null}
        </div>
      ) : null}
      <div className="menu-label eyebrow">Key</div>
      <Legend showTraffic={!!props.layers?.traffic?.on && !props.layers.traffic.hiddenBy} />
    </>
  );
  return (
    <div className="map-controls" onPointerDown={(e) => e.stopPropagation()}>
      <div className="map-controls__group map-controls__group--zoom">
        <IconButton icon="plus" label="Zoom in" variant="overlay" onClick={() => props.onZoom(1)} />
        <IconButton icon="minus" label="Zoom out" variant="overlay" onClick={() => props.onZoom(-1)} />
      </div>
      <div className="map-controls__group">
        {props.onToggleExpanded ? <IconButton icon={props.expanded ? 'close' : 'fit'} label={props.expanded ? 'Exit full-screen map' : 'Full-screen map'} variant="overlay" active={props.expanded} onClick={props.onToggleExpanded} /> : null}
        <button
          type="button"
          className={`icon-btn icon-btn--md icon-btn--overlay map-compass${rotated ? ' is-rotated' : ''}`}
          aria-label={rotated ? 'Reset to north-up' : 'North-up'}
          title={rotated ? 'Reset to north-up' : 'North-up'}
          onClick={props.onNorth}
        >
          <span className="map-compass__needle" style={{ transform: `rotate(${-props.bearing}deg)` }}>
            <svg width="16" height="22" viewBox="0 0 16 22" aria-hidden>
              <path d="M8 1l5 10H3z" fill="var(--lost)" />
              <path d="M8 21l5-10H3z" fill="currentColor" opacity=".55" />
            </svg>
          </span>
        </button>
        {props.viewMode && props.onViewMode ? (
          <button
            type="button"
            className={`icon-btn icon-btn--md icon-btn--overlay map-view3d${props.viewMode === '3d' ? ' is-active' : ''}`}
            aria-pressed={props.viewMode === '3d'}
            aria-label={props.viewMode === '3d' ? '3D view (on) — switch to 2D' : '3D view'}
            title={props.viewMode === '3d' ? 'Switch to 2D' : 'Switch to 3D'}
            data-testid="map-view-3d"
            onClick={() => props.onViewMode!(props.viewMode === '3d' ? '2d' : '3d')}
          >
            <span className="map-view3d__label">3D</span>
          </button>
        ) : null}
        {props.following ? (
          <IconButton
            icon="track-up"
            label={props.trackUp ? 'Track-up (on) — switch to north-up' : 'Track-up'}
            variant="overlay"
            active={props.trackUp}
            onClick={props.onToggleTrackUp}
          />
        ) : null}
        <IconButton icon="fit" label="Fit everything" variant="overlay" onClick={props.onFit} />
        <IconButton ref={anchor} icon="layers" label="Map layers" variant="overlay" onClick={() => setOpen((v) => !v)} active={open} />
      </div>
      {props.expanded ? (open ? <div className="map-basemaps map-basemaps--expanded" role="region" aria-label="Map layers">{body}</div> : null) : phone ? (
        <Modal open={open} onClose={() => setOpen(false)} title="Map" size="sm">
          <div className="map-basemaps map-basemaps--sheet">{body}</div>
        </Modal>
      ) : (
        <Popover open={open} onClose={() => setOpen(false)} anchor={anchor} className="map-basemaps">
          {body}
        </Popover>
      )}
    </div>
  );
}
